import React, { useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';

/**
 * SafeImage — drop-in replacement for `<img>` that:
 *
 *   1. Shows a clearly-labelled "Photo unavailable" tile when src is
 *      empty/null/undefined OR when the underlying `<img>` fires onError
 *      enough times to exhaust the retry budget.
 *   2. Retries transient image-load failures with exponential backoff
 *      BEFORE giving up. Microsoft Graph occasionally 502s under load,
 *      and the corporate firewall in front of office staff can
 *      momentarily drop image responses — both produce a single
 *      `onError` followed by a successful fetch on the next attempt.
 *      Without internal retry, the headless Chromium PDF renderer
 *      would print "Photo unavailable" tiles for those photos and the
 *      user would have to re-trigger the whole "Email me the PDF"
 *      flow. (Added 2026-07 after a 6/19-loaded incident.)
 *   3. Shows a slate skeleton-shimmer overlay while the image is loading
 *      so deferred (loading="lazy") photos don't pop in suddenly when
 *      scrolled into view.
 *
 * Pure display component — no upload, no fetch, no DB writes.
 *
 * Used by the PreCon / Locate / Soft Dig form views and their Preview
 * documents. Pass-through props (alt, loading, decoding, crossOrigin,
 * data-testid, ...) are forwarded to the underlying <img>.
 */

// Retry policy. Three attempts at ~1s, ~3s, ~8s of additional wait
// before showing the fallback. Total ceiling ~12s per image — well
// within the renderer's 5-minute total budget, and effectively
// invisible to users in normal use because most images load on the
// first try. Tuned conservatively to avoid hammering Graph during
// genuine outages.
const RETRY_BACKOFFS_MS = [1000, 3000, 8000];

function appendRetryParam(src, attempt) {
  if (!src) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}_r=${attempt}`;
}

export function SafeImage({ src, alt, className = '', ...rest }) {
  // `failed` flips true ONLY after every retry has been exhausted.
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // The src we're currently trying — starts as the prop, then
  // becomes the prop + cache-buster on each retry. Using a separate
  // state from the prop lets us refetch without remounting the <img>.
  const [currentSrc, setCurrentSrc] = useState(src);
  // Tracks the last src prop value we observed, so we can detect a
  // prop change during render and reset state. This is the React-
  // documented pattern for "reset state when a prop changes" and
  // avoids the `react-hooks/set-state-in-effect` anti-pattern.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevSrc, setPrevSrc] = useState(src);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef(null);

  // Prop-change reset (runs during render, NOT in an effect).
  // Only state setters here — refs are mutated in the effect below.
  if (prevSrc !== src) {
    setPrevSrc(src);
    setFailed(false);
    setLoaded(false);
    setCurrentSrc(src);
  }

  // Reset the retry counter AND cancel any in-flight retry timer
  // whenever `src` changes or the component unmounts. Putting this in
  // an effect keeps render pure (no ref mutation), satisfies the
  // react-hooks/refs lint rule, and prevents a stale setTimeout from
  // overwriting `currentSrc` with the old image's retry URL after the
  // user has already swapped the photo.
  useEffect(() => {
    attemptRef.current = 0;
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [src]);

  const handleError = () => {
    const nextAttempt = attemptRef.current + 1;
    if (nextAttempt > RETRY_BACKOFFS_MS.length) {
      // Budget exhausted → show the fallback.
      setFailed(true);
      return;
    }
    const backoff = RETRY_BACKOFFS_MS[nextAttempt - 1];
    attemptRef.current = nextAttempt;
    // Schedule the next attempt. Append a cache-buster so any cached
    // error response (CDN edge / our /file-proxy retry layer) doesn't
    // just hand us the same failure again. Using `_r=N` keeps the
    // querystring obvious in logs.
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      setCurrentSrc(appendRetryParam(src, nextAttempt));
    }, backoff);
  };

  if (!src || failed) {
    return (
      <div
        className={`${className} !bg-slate-100 flex flex-col items-center justify-center text-slate-400 gap-1 border border-dashed border-slate-300`}
        role="img"
        aria-label={alt || 'Photo unavailable'}
        data-testid="safe-image-unavailable"
      >
        <ImageOff className="w-6 h-6" aria-hidden="true" />
        <span className="text-xs font-medium px-2 text-center">
          Photo unavailable
        </span>
      </div>
    );
  }

  return (
    // The caller-supplied className (w-*, h-*, rounded, border, ...) is
    // applied to the WRAPPER so this component occupies the exact same
    // box the bare <img> would. Inner <img> and skeleton are positioned
    // absolutely to fill it — prevents the wrapper from collapsing or
    // expanding unexpectedly inside grid / flex containers (which was
    // pushing the FieldReportHeader right column off the preview page).
    <div className={`${className} relative overflow-hidden`}>
      <img
        src={currentSrc}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={handleError}
        {...rest}
      />
      {!loaded && (
        <div
          className="absolute inset-0 !bg-slate-200 animate-pulse"
          aria-hidden="true"
          data-testid="safe-image-loading"
        />
      )}
    </div>
  );
}

export default SafeImage;
